import { Component, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { COMPLETE_DEADLINE_SITUATIONS, RATINGS, RESPONSE_DEADLINE_SITUATIONS, STATUS } from '@consts';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { searchManyInputStyles } from '@core/components/fc-input/base/fc-search-many-input/fc-search-many-input.component';
import { Label, MaintenanceDeadlineStatusEnum, PendencyType, Problem, User, WarrantiesStatusEnum } from '@core/interfaces';
import { PaymentStatus } from '@core/interfaces/payments';
import {
  clearEmptyProperties,
  endOfDay,
  mapCheckbox,
  mapIds,
  parseBoolean,
  parseDate,
  parseIdsToBooleanArray,
  splitStringByComma,
  startOfDay
} from '@core/utils/filters-utils';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { Company, MaintenanceType, Segment } from '@interfaces';
import { TranslateService } from '@ngx-translate/core';
import { DateIntervalValidation } from '@validators/date-interval-validation';
import { DatePeriodValidation } from '@validators/date-period-validation';
import { DateValidation } from '@validators/date.validation';
import { cloneDeep, isEmpty } from 'lodash';
import moment from 'moment';
import { Subject } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { LocationGroup } from './../../../core/interfaces/locations/index';
import english from './../i18n/en.json';
import spanish from './../i18n/es.json';
import portuguese from './../i18n/pt.json';
import { MaintenanceFilterApi } from './services/maintenance-filter-api.service';
import { MaintenanceFilterState } from './services/maintenance-filter-state.service';

@Component({
  selector: 'filter-maintenances',
  templateUrl: './filter-maintenances.component.html',
  styleUrls: ['./filter-maintenances.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class FilterMaintenanceComponent implements OnInit {
  @Output() changeView = new Subject<string>();
  public filtersForm: FormGroup;
  public problems: Problem[] = [];
  public assignees: User[] = [];
  public requesters: User[] = [];
  public companies: Company[] = [];
  public locationsGroups: LocationGroup[] = [];
  public maintenanceTypes: MaintenanceType[] = [];
  public segments: Segment[] = [];
  public labels: Label[] = [];
  public desiredStyle: searchManyInputStyles = searchManyInputStyles.Outlined;

  public STATUS = STATUS;
  public RATINGS = RATINGS;
  public COMPLETE_DEADLINE_SITUATIONS = COMPLETE_DEADLINE_SITUATIONS;
  public RESPONSE_DEADLINE_SITUATIONS = RESPONSE_DEADLINE_SITUATIONS;
  public PAYMENT_STATUS = Object.values(PaymentStatus);
  public maintenanceDeadlineStatusList = Object.keys(MaintenanceDeadlineStatusEnum);
  public warrantyStatus = Object.keys(WarrantiesStatusEnum);

  public hasLabelViewPermission = this.authenticationService.hasPermission('labels-list');
  public hasLocationGroupsViewGroupPermission = this.authenticationService.hasPermission('locations-groups-list');
  public hasFlagWarranties = this.authenticationService.hasFlags(['warranties']);

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly activatedRoute: ActivatedRoute,
    private readonly translateService: TranslateService,
    private readonly maintenanceFilterApi: MaintenanceFilterApi,
    private readonly maintenanceFilterState: MaintenanceFilterState,
    private readonly authenticationService: AuthenticationService,
    private readonly fuseTranslationLoaderService: FuseTranslationLoaderService
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  public async ngOnInit(): Promise<void> {
    const data = await this.maintenanceFilterApi.getFilterData();

    this.labels = data.labels;
    this.problems = data.problems;
    this.companies = data.companies;
    this.assignees = data.assignees;
    this.requesters = data.requesters;
    this.locationsGroups = data.locationsGroups;
    this.maintenanceTypes = data.maintenanceTypes;
    this.segments = data.segments;

    this.filtersForm = this.formBuilder.group({
      number: null,
      message: null,
      openedAtLte: [moment(), [Validators.required, DateValidation.validate]],
      openedAtGte: [moment().subtract(1, 'years'), [Validators.required, DateValidation.validate]],
      closedAtGte: [null, DateValidation.validate],
      closedAtLte: [null, DateValidation.validate],
      scheduledAtGte: [null, DateValidation.validate],
      scheduledAtLte: [null, DateValidation.validate],
      hasEquipments: new FormControl(''),
      archived: new FormControl(false),
      assignees: new FormControl([]),
      segmentId: new FormControl([]),
      requesters: new FormControl([]),
      companies: new FormControl([]),
      equipmentId: new FormControl(),
      locations: new FormControl([]),
      status: new FormArray(STATUS.map(() => new FormControl(false))),
      ratings: new FormArray(RATINGS.map(() => new FormControl(false))),
      segments: new FormControl([]),
      paymentStatus: new FormArray(this.PAYMENT_STATUS.map(() => new FormControl(false))),
      paymentNumber: new FormControl(''),
      pendenciesTreatmentStatus: new FormControl(''),
      pendenciesTypes: new FormControl(''),
      pendenciesStatus: new FormControl(''),
      firstVisitOnPeriodSituation: new FormArray(RESPONSE_DEADLINE_SITUATIONS.map(() => new FormControl(false))),
      closeOnPeriodSituation: new FormArray(COMPLETE_DEADLINE_SITUATIONS.map(() => new FormControl(false))),
      locationsGroups: new FormArray(this.locationsGroups.map(() => new FormControl(false))),
      maintenanceTypes: new FormArray(this.maintenanceTypes.map(() => new FormControl(false))),
      problems: new FormArray(this.problems.map(() => new FormControl(false))),
      labels: new FormArray(this.labels.map(() => new FormControl(false))),
      warrantyMaintenanceStatus: new FormControl(''),
      warrantyStartDateLte: [null, DateValidation.validate],
      warrantyEndDateGte: [null, DateValidation.validate],
      warrantyStatusIn: new FormArray(this.warrantyStatus.map(() => new FormControl(false))),
    }, {
      validators: [
        DatePeriodValidation.validate(['openedAt', 'closedAt', 'scheduledAt']),
        DateIntervalValidation.validate({
          from: 'openedAtGte',
          to: 'openedAtLte',
          maxDiffLte: 1,
          unit: 'year'
        }),
      ]
    });

    this.filtersForm.addControl('responseDeadlineStatus', new FormArray(this.maintenanceDeadlineStatusList.map(() => new FormControl(false))));
    this.filtersForm.addControl('solutionDeadlineStatus', new FormArray(this.maintenanceDeadlineStatusList.map(() => new FormControl(false))));

    this.maintenanceFilterState.onClearFilter.subscribe(() => {
      this.filtersForm.reset();
      this.filtersForm.get('archived').setValue(false);
      this.filtersForm.get('hasEquipments').setValue('');
      this.filtersForm.get('openedAtLte').setValue(moment());
      this.filtersForm.get('openedAtGte').setValue(moment().subtract(1, 'years'));
    });

    this.filtersForm.valueChanges
      .pipe(debounceTime(400), filter(() => this.filtersForm.valid))
      .subscribe(() => {
        const value = this.filtersForm.getRawValue();
        const openedAtGte = value.openedAtGte;
        const openedAtLte = value.openedAtLte;
        const closedAtGte = value.closedAtGte;
        const closedAtLte = value.closedAtLte;
        const scheduledAtGte = value.scheduledAtGte;
        const scheduledAtLte = value.scheduledAtLte;
        const warrantyStartDateGte = value.warrantyStartDateLte;
        const warrantyEndDateLte = value.warrantyEndDateGte;
        this.maintenanceFilterState.query = {
          number_contains: value.number,
          companyId_in: mapIds(value.companies),
          assigneeId_in: value.assignees,
          message_contains: value.message,
          equipmentId_in: value.equipmentId,
          locationId_in: mapIds(value.locations),
          requesterId_in: mapIds(value.requesters),
          openedAt_gte: startOfDay(openedAtGte),
          openedAt_lte: endOfDay(openedAtLte),
          closedAt_gte: startOfDay(closedAtGte),
          closedAt_lte: endOfDay(closedAtLte),
          archived: value.archived,
          has_equipments: value.hasEquipments,
          scheduledAt_gte: startOfDay(scheduledAtGte, 'YYYY-MM-DD'),
          scheduledAt_lte: endOfDay(scheduledAtLte, 'YYYY-MM-DD'),
          status_in: mapCheckbox(value.status, this.STATUS, 'name'),
          typeId_in: mapCheckbox(value.maintenanceTypes, this.maintenanceTypes, 'id'),
          problemId_in: mapCheckbox(value.problems, this.problems, 'id'),
          labelId_in: mapCheckbox(value.labels, this.labels, 'id'),
          rating_in: mapCheckbox(value.ratings, this.RATINGS, 'value'),
          segmentId_in: mapIds(value.segments),
          paymentStatus_in: mapCheckbox(value.paymentStatus, this.PAYMENT_STATUS),
          paymentNumber_contains: value.paymentNumber,
          pendenciesTreatmentStatus_in: value.pendenciesTreatmentStatus,
          pendenciesTypeId_in: mapIds(value.pendenciesTypes),
          pendenciesStatus_in: value.pendenciesStatus,
          locationsGroupsId_in: mapCheckbox(value.locationsGroups, this.locationsGroups, 'id'),
          firstVisitStartedOnPeriod: this.getFirstVisitOnPeriodFilterValue(),
          closedOnPeriod: this.getCloseOnPeriodOnPeriodFilterValue(),
          warrantyMaintenanceStatus: value.warrantyMaintenanceStatus,
          warrantyStartDateLte: endOfDay(warrantyStartDateGte, 'YYYY-MM-DD'),
          warrantyEndDateGte: startOfDay(warrantyEndDateLte, 'YYYY-MM-DD'),
          warrantyStatusIn: mapCheckbox(value.warrantyStatusIn, this.warrantyStatus),
        };
        this.maintenanceFilterState.query.responseDeadlineStatus_in = mapCheckbox(value.responseDeadlineStatus, this.maintenanceDeadlineStatusList) as MaintenanceDeadlineStatusEnum[];
        this.maintenanceFilterState.query.solutionDeadlineStatus_in = mapCheckbox(value.solutionDeadlineStatus, this.maintenanceDeadlineStatusList) as MaintenanceDeadlineStatusEnum[];

        this.maintenanceFilterState.query = clearEmptyProperties(this.maintenanceFilterState.query);
        this.maintenanceFilterState.onChangeFilter.next();
      });

    const queryParams = cloneDeep(this.activatedRoute.snapshot.queryParams || {});

    Reflect.deleteProperty(queryParams, 'id');
    Reflect.deleteProperty(queryParams, 'selectedTab');

    if (!isEmpty(queryParams)) {
      const {
        number_contains,
        archived,
        has_equipments,
        status_in,
        typeId_in,
        segmentId_in,
        locationId_in,
        locationGroupId_in,
        companyId_in,
        openedAt_gte,
        openedAt_lte,
        scheduledAt_gte,
        scheduledAt_lte,
        closedAt_gte,
        closedAt_lte,
        responsePeriod,
        solutionPeriod
      } = queryParams;

      const {
        closeOnPeriodSituation,
        firstVisitOnPeriodSituation,
        mappedResponseStatus,
        mappedSolutionStatus
      } = this.getResponseAndSolutionQueryParam({ responsePeriod, solutionPeriod });

      const locationIds = splitStringByComma(locationId_in);
      const locations = locationIds.length ? await this.maintenanceFilterApi.findLocations(locationIds) : [];
      const companyIds = splitStringByComma(companyId_in);
      const companies = companyIds.length ? companyIds.map((id) => ({ id })) : [];

      this.filtersForm.reset();
      this.filtersForm.patchValue({
        number: number_contains || null,
        archived: parseBoolean(archived),
        hasEquipments: has_equipments,
        status: parseIdsToBooleanArray(this.STATUS, status_in, 'name'),
        maintenanceTypes: parseIdsToBooleanArray(this.maintenanceTypes, typeId_in, 'id'),
        locations: locations || [],
        locationsGroups: parseIdsToBooleanArray(this.locationsGroups, locationGroupId_in, 'id'),
        companies: companies || [],
        openedAtGte: parseDate(openedAt_gte),
        openedAtLte: parseDate(openedAt_lte),
        closedAtGte: parseDate(closedAt_gte),
        closedAtLte: parseDate(closedAt_lte),
        scheduledAtGte: parseDate(scheduledAt_gte),
        scheduledAtLte: parseDate(scheduledAt_lte),
        firstVisitOnPeriodSituation: firstVisitOnPeriodSituation,
        closeOnPeriodSituation: closeOnPeriodSituation,
        responseDeadlineStatus: mappedResponseStatus,
        solutionDeadlineStatus: mappedSolutionStatus,
      });
    }
  }

  public firstVisitOnPeriodOnChange(selectedIndex: number): void {
    const firstVisitOnPeriodControls = this.filtersForm.controls['firstVisitOnPeriodSituation'] as FormArray;

    firstVisitOnPeriodControls.controls.forEach((control, controlIndex) => {
      if (controlIndex !== selectedIndex) {
        control.setValue(false);
      }
    });
  }

  public closeOnPeriodOnChange(selectedIndex: number): void {
    const closeOnPeriodControls = this.filtersForm.controls['closeOnPeriodSituation'] as FormArray;
    closeOnPeriodControls.controls.forEach((control, controlIndex) => {
      if (controlIndex !== selectedIndex) {
        control.setValue(false);
      }
    });
  }

  getFirstVisitOnPeriodFilterValue(): boolean {
    const firstVisitOnPeriod = this.filtersForm.controls['firstVisitOnPeriodSituation'] as FormArray;

    let selectedIndex = null;
    for (let index = 0; index < firstVisitOnPeriod.value.length; index++) {
      const value = firstVisitOnPeriod.value[index];
      if (value) {
        selectedIndex = index;
        break;
      }
    }

    if (selectedIndex === null) {
      return undefined;
    }

    return RESPONSE_DEADLINE_SITUATIONS[selectedIndex].value;
  }

  getLabelName(label: any, index: number): string {
    if (index === 0) {
      return this.translateService.instant('MAINTENANCES.FILTERS.NO_LABELS');
    } else {
      return label.name;
    }
  }

  getCloseOnPeriodOnPeriodFilterValue(): boolean {
    const closeOnPeriodControl = this.filtersForm.controls['closeOnPeriodSituation'] as FormArray;

    let selectedIndex = null;
    for (let index = 0; index < closeOnPeriodControl.value.length; index++) {
      const value = closeOnPeriodControl.value[index];
      if (value) {
        selectedIndex = index;
        break;
      }
    }

    if (selectedIndex === null) {
      return undefined;
    }

    return COMPLETE_DEADLINE_SITUATIONS[selectedIndex].value;
  }

  getPropertyValue(value, array, property): any {
    const item = array.find((a) => a.id === value);
    return item[property];
  }

  private getResponseAndSolutionQueryParam({ responsePeriod, solutionPeriod }): any {
    const firstVisitOnPeriodSituation = [null, null];
    const closeOnPeriodSituation = [null, null];
    const responseDeadlineStatus = [];
    const solutionDeadlineStatus = [];

    if (responsePeriod === 'on') {
      firstVisitOnPeriodSituation[0] = true;
      responseDeadlineStatus.push('completedOnTime', 'atRisk', 'withinDeadline');
    }

    if (responsePeriod === 'out') {
      firstVisitOnPeriodSituation[1] = true;
      responseDeadlineStatus.push('violated', 'completedWithViolation');
    }

    if (solutionPeriod === 'on') {
      closeOnPeriodSituation[0] = true;
      solutionDeadlineStatus.push('completedOnTime', 'atRisk', 'withinDeadline');
    }

    if (solutionPeriod === 'out') {
      closeOnPeriodSituation[1] = true;
      solutionDeadlineStatus.push('violated', 'completedWithViolation');
    }

    if (responsePeriod === 'notApplied' || solutionPeriod === 'notApplied') {
      firstVisitOnPeriodSituation[0] = true;
      firstVisitOnPeriodSituation[1] = true;

      solutionDeadlineStatus.push('notApplied');
      responseDeadlineStatus.push('notApplied');
    }

    const mappedSolutionStatus = this.maintenanceDeadlineStatusList.map((status) => solutionDeadlineStatus.includes(status));
    const mappedResponseStatus = this.maintenanceDeadlineStatusList.map((status) => responseDeadlineStatus.includes(status));

    return { firstVisitOnPeriodSituation, closeOnPeriodSituation, mappedSolutionStatus, mappedResponseStatus };
  }
}
